<template>
  <deep-chat
    ref="chat-view"
    :auxiliaryStyle="`${auxiliaryStyle} ${feedbackButtonsStyles}`"
    :avatars.prop="{ ai: aiAvatarStyle, user: userAvatarStyle }"
    :class="isDeepChatReady ? 'view-deep-chat' : 'no-view'"
    :connect.prop="{ handler: sendMessage, stream: true }"
    :customButtons.prop="customButtons"
    :displayLoadingBubble.prop="true"
    :errorMessages.prop="{ displayServiceErrorMessages: true }"
    :id="id"
    :loadHistory.prop="loadHistoryConversation"
    :messageStyles.prop="messageStyles"
    :style="chatViewStyles"
    :submitButtonStyles.prop="submitButtonStyles"
    :textInput.prop="textInputStyles"
  >
  </deep-chat>
</template>

<script>
import 'deep-chat'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import { IMAGE_SIZES } from 'rever-media-sdk'

import api from '@/api'
import MediaSDK from '@/lib/ReverMediaSDK'
import mixin from '@/mixins/mixin.vue'

import faker from './aiFaker'
import deepChatStylesMixin from './deepChatStylesMixin.vue'
import feedbackMixin from './feedbackMixin.vue'

const AI_ROLE_FOR_MESSAGES = 'ai'
const USER_ROLE_FOR_MESSAGES = 'user'
const LIMIT_MESSAGES_PER_PAGE = 20
const TITLE_MAX_LENGTH = 50
const TIMEOUT_DURATION_IN_MS = 30 * 1000 // 30 seconds
const TIMEOUT_ERROR_CODE = 'TimeoutError'
const ABORT_ERROR_CODE = 'AbortError'

export default {
  name: 'AIChat',

  mixins: [mixin, deepChatStylesMixin, feedbackMixin],

  props: {
    useFakeResponses: {
      type: Boolean,
      default: false,
    },

    pastConversationId: {
      type: String,
      default: null,
    },

    hasTokensAvailable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      callSignals: {},
      chatUUID: '',
      fetchedImgSrc: '',
      history: [],
      id: 'chat-view',
      IMAGE_SIZES,
      isDeepChatReady: false,
      requestAbortController: new AbortController(),
      timeoutId: null,
      isRequestActive: false,
    }
  },

  async mounted() {
    this.getPhoto()
    await this.checkDOMReady()
  },

  computed: {
    ...mapGetters(['account', 'organizationId']),

    accountPhoto() {
      return this.account?.photo ?? null
    },

    userAvatarStyle() {
      return {
        src: this.fetchedImgSrc,
        styles: {
          avatar: {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            padding: '0px',
          },
          container: {
            overflow: 'clip',
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
            width: '33px',
            height: '33px',
            marginTop: '9px',
            borderRadius: '50%',
          },
        },
      }
    },

    deepChatRef() {
      return this.$refs['chat-view']
    },
  },

  watch: {
    pastConversationId: {
      handler(newVal) {
        if (newVal) {
          this.loadNewPastConversation()
        }
      },
      immediate: false,
    },
  },

  methods: {
    async checkDOMReady() {
      const shadowRoot = this.deepChatRef.shadowRoot
      const container = shadowRoot.querySelector('#container')
      const chatView = container.querySelector('#chat-view')

      this.isDeepChatReady = Boolean(chatView)

      if (this.isDeepChatReady) {
        this.$emit('isReady', this.isDeepChatReady)
        return
      }

      setTimeout(this.checkDOMReady, 500)
    },

    handlePromptInteraction(text) {
      this.deepChatRef.submitUserMessage({ text })
    },

    async sendMessage(body, signals) {
      this.$emit('userInteracted')
      this.callSignals = signals

      signals.onOpen()
      signals.stopClicked.listener = this.handleAbort

      const trimmedMessage = body.messages[0]?.text?.trim?.()

      if (!trimmedMessage) {
        signals.onClose()
        return
      }

      if (!this.chatUUID) {
        const UUID = this.useFakeResponses
          ? await faker.createNewChat()
          : await api.ai.createNewChat(this.organizationId, {
              title: trimmedMessage.slice(0, TITLE_MAX_LENGTH),
            })
        this.chatUUID = UUID?.data?.threadId
      }

      if (this.useFakeResponses) {
        await this.simulateResponse(signals, trimmedMessage)
        return
      }

      try {
        this.isRequestActive = true

        const response = await Promise.race([
          this.getTimeoutPromise(),
          this.sendAPIMessageRequest(trimmedMessage),
        ])

        const isErrorResponse = response.status > 200

        if (isErrorResponse) {
          await this.handleErrorResponse(response)
        } else {
          await this.decodeSuccessResponse(response, signals, trimmedMessage)
        }
      } catch (error) {
        this.isRequestActive = false
        this.handleError(error)
      } finally {
        signals.onClose()

        if (this.timeoutId) {
          clearTimeout(this.timeoutId)
          this.timeoutId = null
        }
      }
    },

    async sendAPIMessageRequest(message) {
      try {
        const response = await api.ai.postMessage(
          this.organizationId,
          {
            threadId: this.chatUUID,
            message,
          },
          this.requestAbortController?.signal
        )

        // If request timeouted or was aborted, cancel the response stream to prevent memory leaks
        if (!this.isRequestActive) {
          await response?.body?.cancel?.()
        }
        return response
      } catch (error) {
        if (!this.isRequestActive) return
        throw error
      }
    },

    getTimeoutPromise() {
      return new Promise((_resolve, reject) => {
        this.timeoutId = setTimeout(() => {
          const error = new Error()
          error.name = TIMEOUT_ERROR_CODE
          reject(error)
        }, TIMEOUT_DURATION_IN_MS)
      })
    },

    async handleErrorResponse(response) {
      const { error, message, logId } = await this.decodeErrorResponse(response)

      const exceptionError = new Error()
      exceptionError.response = response
      exceptionError.message = message
      exceptionError.error = error
      exceptionError.logId = logId

      throw exceptionError
    },

    async simulateResponse(signals, userMessage) {
      let messageId = null
      let fullText = ''

      for await (const chunk of faker.sendMessage()) {
        messageId = chunk.id
        fullText += chunk.content + ' '

        signals.onResponse({
          html: this.getHTMLMessageWithoutFeedbackButtons(fullText),
          role: AI_ROLE_FOR_MESSAGES,
          overwrite: true,
        })
      }

      // Final bubble's content override will include feedback buttons, whose HTML generation implies extra processing
      await signals.onResponse({
        html: this.getHTMLMessageWithFeedbackButtons(fullText, {
          threadId: this.chatUUID,
          messageId,
          userMessage,
        }),
        role: AI_ROLE_FOR_MESSAGES,
        overwrite: true,
      })

      signals.onClose()
    },

    async decodeSuccessResponse(response, signals, userMessage) {
      const decoder = new TextDecoder()
      let fullText = ''
      let messageId = null

      for await (const chunk of response?.body ?? []) {
        const decodedChunk = decoder.decode(chunk)
        const stringJSONs = decodedChunk.split(/(?<=})\s*(?={)/)

        stringJSONs.forEach(jsonString => {
          const parsed = this.parseJSONString(jsonString)

          fullText += parsed.content
          messageId = parsed.id || messageId

          signals.onResponse({
            html: this.getHTMLMessageWithoutFeedbackButtons(fullText),
            role: AI_ROLE_FOR_MESSAGES,
            overwrite: true,
          })
        })
      }

      // Final bubble's content override will include feedback buttons, whose HTML generation implies extra processing
      await signals.onResponse({
        role: AI_ROLE_FOR_MESSAGES,
        overwrite: true,
        html: this.getHTMLMessageWithFeedbackButtons(fullText, {
          threadId: this.chatUUID,
          messageId,
          userMessage,
        }),
      })
    },

    async decodeErrorResponse(response) {
      const decoder = new TextDecoder()
      let message = ''
      let error = ''
      let logId = ''

      for await (const chunk of response?.body ?? []) {
        const decodedChunk = decoder.decode(chunk)
        const stringJSONs = decodedChunk.split(/(?<=})\s*(?={)/)

        stringJSONs.forEach(jsonString => {
          const parsedJSON = this.parseJSONString(jsonString)
          error += parsedJSON.error ?? ''
          message += parsedJSON.message ?? ''
          logId += parsedJSON.logId ?? ''
        })
      }

      return { error, message, logId }
    },

    parseJSONString(jsonString) {
      try {
        return JSON.parse(jsonString)
      } catch (error) {
        return {}
      }
    },

    handleError(error) {
      if (error?.name === ABORT_ERROR_CODE) {
        return
      }

      if (error?.name === TIMEOUT_ERROR_CODE) {
        this.presentTimeoutMessage()
        this.handleAbort()
        return
      }

      if (error?.response?.status === 403) {
        this.handleInsufficientTokensError()
        return
      }

      this.deepChatRef.addMessage({ error: this.getErrorText(error) })
    },

    presentTimeoutMessage() {
      this.callSignals.onResponse({
        text: this.$t('ai_features.chat_view.timeout_message'),
        role: AI_ROLE_FOR_MESSAGES,
      })
    },

    getErrorText(error) {
      const errorMessage = `${error?.message ??
        error.error ??
        error?.response?.message}${
        error?.logId ? ` (Log id:${error.logId})` : ''
      }`
      const errorCode = error?.status ?? error?.response?.status

      if (errorMessage) {
        return this.$t('ai_features.chat_view.generic_error_with_message', {
          errorMessage,
        })
      }

      if (errorCode) {
        return this.$t('ai_features.chat_view.generic_error_with_code', {
          errorCode,
        })
      }

      return this.$t('ai_features.chat_view.generic_error')
    },

    handleInsufficientTokensError() {
      this.deepChatRef.addMessage({
        error: this.$t('ai_features.chat_view.insufficient_tokens'),
      })
      this.deepChatRef.disableSubmitButton()
      this.deepChatRef.shadowRoot.querySelector(
        '#text-input'
      ).style.pointerEvents = 'none'

      this.$emit('insufficientTokens')
    },

    async getPhoto() {
      if (_.isEmpty(this.accountPhoto)) return

      this.fetchedImgSrc = await MediaSDK.getImageSrc(
        this.accountPhoto,
        this.IMAGE_SIZES.MINI
      )
    },

    clearMessages() {
      this.handleAbort()
      this.deepChatRef.clearMessages()
      this.checkTokensAvailability()
      this.chatUUID = null
    },

    parseMessages(messages) {
      return messages
        .map((message, index) => {
          return message.messageType === 'ai'
            ? {
                role: AI_ROLE_FOR_MESSAGES,
                html: this.getHTMLMessageWithFeedbackButtons(message.content, {
                  threadId: this.chatUUID,
                  messageId: message.id,
                  userMessage: messages[index + 1]?.content || '', // Since latest message is first in array, we have to look at the next one to try to get the user message
                }),
              }
            : {
                role:
                  message.messageType === 'human'
                    ? USER_ROLE_FOR_MESSAGES
                    : AI_ROLE_FOR_MESSAGES,
                text: message.content,
              }
        })
        .reverse()
    },

    async loadNewPastConversation() {
      this.id = `chat-view-${Date.now()}`
    },

    async loadHistoryConversation(index) {
      try {
        if (!this.pastConversationId) return []

        this.$emit('userInteracted')

        this.chatUUID = this.pastConversationId

        const messages = await api.ai.getChatMessages(
          this.organizationId,
          this.pastConversationId,
          {
            limit: LIMIT_MESSAGES_PER_PAGE,
            skip: index * LIMIT_MESSAGES_PER_PAGE,
          }
        )

        const conversationMessages = this.parseMessages(messages.data.results)

        const hasMoreMessages =
          messages.data.totalRecordsInData >
          (index + 1) * LIMIT_MESSAGES_PER_PAGE

        // If more messages are available, push a `false` to let deep chat know. https://deepchat.dev/docs/interceptors#loadHistory
        if (hasMoreMessages) {
          conversationMessages.push(false)
        }

        this.$emit('hideLoading')
        this.checkTokensAvailability()

        return conversationMessages
      } catch (error) {
        this.showError(error)
      }
    },

    checkTokensAvailability() {
      if (!this.hasTokensAvailable) {
        this.deepChatRef.addMessage({
          error: this.$t('ai_features.chat_view.insufficient_tokens'),
        })
        this.deepChatRef.disableSubmitButton()
        this.deepChatRef.shadowRoot.querySelector(
          '#text-input'
        ).style.pointerEvents = 'none'
      }
    },

    handleAbort() {
      this.requestAbortController?.abort?.()
      this.callSignals?.onClose?.()
      this.requestAbortController = new AbortController()
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.no-view {
  display: none;
}

.view-deep-chat {
  display: flexbox;
}
</style>
